import { Badge, Tooltip } from '@mui/material';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import { DateTime } from 'luxon';

const InitiativeTimestampSummaryIndicator = ({
  timestampSummary,
  initiative,
}) => {
  if (!timestampSummary || !initiative) return null;

  const { maxEarliestDataTimestamp, minLatestDataTimestamp } = timestampSummary;

  const baselineStart = DateTime.fromISO(
    initiative.baselineStart || '',
  ).setZone('utc');
  const comparisonEnd = DateTime.fromISO(
    initiative.comparisonEnd || '',
  ).setZone('utc');
  const maxEarliest = DateTime.fromISO(maxEarliestDataTimestamp || '').setZone(
    'utc',
  );
  const minLatest = DateTime.fromISO(minLatestDataTimestamp || '').setZone(
    'utc',
  );
  const now = DateTime.now().setZone('utc');

  const state = initiative.state;

  const hasValidBaselineCheck = baselineStart.isValid && maxEarliest.isValid;
  const hasValidComparisonCheck = comparisonEnd.isValid && minLatest.isValid;

  const shouldCheckBaseline =
    state !== 'Created' && state !== 'Archived' && hasValidBaselineCheck;

  const shouldCheckComparison = state === 'Done' && hasValidComparisonCheck;

  // Rule 1: baselineStart must be > maxEarliestDataTimestamp
  const baselineIssue = shouldCheckBaseline && baselineStart <= maxEarliest;

  // Rule 2: comparisonEnd must be < minLatestDataTimestamp (only for "Done")
  const comparisonIssue = shouldCheckComparison && comparisonEnd >= minLatest;

  let tooltipTitle = '';
  let BadgeIcon = null;
  let badgeColor = 'warning';

  if (baselineIssue) {
    // Rule 1: use Warning badge
    BadgeIcon = WarningRoundedIcon;
    badgeColor = 'warning';
    tooltipTitle =
      'Baseline-perioden starter før de tidligst tilgængelige data. ' +
      'Dette kan indikere en fejl i datagrundlaget eller manglende historiske data.';
  } else if (comparisonIssue) {
    // Rule 2: split by age of comparisonEnd
    const diffDays = now.diff(comparisonEnd, 'days').days;

    if (diffDays >= 0 && diffDays < 30) {
      // Less than 30 days old → Info badge
      BadgeIcon = InfoRoundedIcon;
      badgeColor = 'info';
      tooltipTitle =
        'Initiativet sluttede for mindre end 30 dage siden, og der kan stadig ' +
        'mangle eller være forsinket data i slutningen af perioden.';
    } else if (diffDays >= 30) {
      // 30+ days old → Warning badge
      BadgeIcon = WarningRoundedIcon;
      badgeColor = 'warning';
      tooltipTitle =
        'Initiativets slutdato ligger efter den senest fuldt synkroniserede periode. ' +
        'Resultaterne kan være upålidelige på grund af manglende eller usikre data.';
    } else {
      // comparisonEnd is in the future – treat as info/warning too
      BadgeIcon = InfoRoundedIcon;
      badgeColor = 'info';
      tooltipTitle =
        'Initiativets slutdato ligger i fremtiden i forhold til de tilgængelige data. ' +
        'Der kan mangle data til den afsluttende periode.';
    }
  }

  // Nothing to show
  if (!BadgeIcon || !tooltipTitle) return null;

  return (
    <Tooltip
      title={tooltipTitle}
      componentsProps={{
        tooltip: {
          sx: {
            fontSize: '14px', // affects the tooltip text
          },
        },
      }}
    >
      <Badge
        overlap="circular"
        badgeContent={<BadgeIcon fontSize="small" color={badgeColor} />}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <AccessTimeRoundedIcon />
      </Badge>
    </Tooltip>
  );
};

export default InitiativeTimestampSummaryIndicator;
